import * as React from 'react';
import { Button, Switch } from '@heroui/react';
import { IconPlus } from '@tabler/icons-react';
import { QuestionEditor } from './QuestionEditor';
import { SectionEditor } from './SectionEditor';
import type { Question, QuestionnaireType, Section } from './types';

const emptyChoiceQuestion = (): Question =>
  ({
    id: '',
    type: 'single_choice',
    title: '',
    required: false,
    options: [],
  }) as Question;

export interface QuestionDesignSectionProps {
  useSections: boolean;
  sections: Section[];
  questions: Question[];
  questionnaireType: QuestionnaireType;
  disabled?: boolean;
  onUseSectionsChange: (v: boolean) => void;
  onAddSection: () => void;
  onRemoveSection: (sectionId: string) => void;
  onUpdateSection: (sectionId: string, updates: Partial<Section>) => void;
  onAddQuestion: (question: Question, sectionId?: string) => void;
  onRemoveQuestion: (questionId: string, sectionId?: string) => void;
  onUpdateQuestion: (questionId: string, updates: Partial<Question>, sectionId?: string) => void;
}

export function QuestionDesignSection({
  useSections,
  sections,
  questions,
  questionnaireType,
  disabled,
  onUseSectionsChange,
  onAddSection,
  onRemoveSection,
  onUpdateSection,
  onAddQuestion,
  onRemoveQuestion,
  onUpdateQuestion,
}: QuestionDesignSectionProps) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-lg font-semibold">题目设计</span>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">启用章节设计</span>
          <Switch isSelected={useSections} onChange={onUseSectionsChange} isDisabled={disabled}>
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Content>
          </Switch>
        </div>
      </div>

      {useSections ? (
        <>
          {sections.map((section, index) => (
            <SectionEditor
              key={section.id}
              section={section}
              sectionIndex={index + 1}
              questionnaireType={questionnaireType}
              disabled={disabled}
              onUpdateName={(name) => onUpdateSection(section.id, { name })}
              onUpdateDescription={(description) => onUpdateSection(section.id, { description })}
              onQuestionUpdate={(qid, u) => onUpdateQuestion(qid, u, section.id)}
              onQuestionRemove={(qid) => onRemoveQuestion(qid, section.id)}
              onAddQuestion={() => onAddQuestion(emptyChoiceQuestion(), section.id)}
              onRemove={() => onRemoveSection(section.id)}
            />
          ))}
          <Button type="button" variant="secondary" onPress={onAddSection} isDisabled={disabled} className="w-full">
            <IconPlus className="mr-2 size-4" />
            添加章节
          </Button>
        </>
      ) : (
        <>
          {questions.map((question, index) => (
            <div key={question.id} className="rounded-box border border-divider p-4">
              <QuestionEditor
                question={question}
                questionIndex={index + 1}
                questionnaireType={questionnaireType}
                disabled={disabled}
                onUpdate={(u) => onUpdateQuestion(question.id, u)}
                onRemove={() => onRemoveQuestion(question.id)}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onPress={() => onAddQuestion(emptyChoiceQuestion())}
            isDisabled={disabled}
            className="w-full"
          >
            <IconPlus className="mr-2 size-4" />
            添加题目
          </Button>
        </>
      )}
    </div>
  );
}
